import type { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Quaternion } from '@babylonjs/core/Maths/math.vector';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import type { Scene } from '@babylonjs/core/scene';
import { WebXRDefaultExperience } from '@babylonjs/core/XR/webXRDefaultExperience';
import { WebXRFeaturesManager } from '@babylonjs/core/XR/webXRFeaturesManager';
import { WebXRState } from '@babylonjs/core/XR/webXRTypes';
import type { WebXRCamera } from '@babylonjs/core/XR/webXRCamera';
import type { WebXRInputSource } from '@babylonjs/core/XR/webXRInputSource';
import type { WebXRAbstractMotionController } from '@babylonjs/core/XR/motionController/webXRAbstractMotionController';

import { isImmersiveVrSupported } from './XrSupport';

// Tree-shaken builds only register the features that are imported. Without these the
// session starts with no teleportation and no pointer, and nothing says why.
import '@babylonjs/core/XR/features/WebXRControllerTeleportation';
import '@babylonjs/core/XR/features/WebXRControllerPointerSelection';
import '@babylonjs/core/XR/motionController/webXRGenericMotionController';
import '@babylonjs/core/XR/motionController/webXRProfiledMotionController';

// Runtime dependencies of those features, which tree-shaking also drops. Importing the
// feature module is not enough — it registers, reports as available, and then throws on
// construction because a Scene method it calls was shaken out:
//   animatable  -> scene.beginAnimation, used by the teleportation target reticle
//   ray         -> scene.pickWithRay, used by teleport aiming and pointer selection
import '@babylonjs/core/Animations/animatable';
import '@babylonjs/core/Culling/ray';

// Controller models are .glb files fetched from the WebXR input profile registry, so
// without a glTF loader registered you get tracked-but-invisible controllers.
import '@babylonjs/loaders/glTF/2.0';

import { handedness, type HandRole } from '../input/Handedness';
import { seated } from '../input/Seated';
import { quatToArray, vec3ToArray } from '../net/packing';

/**
 * 60 degrees. Small enough to keep bearings, large enough to be worth the press — smooth
 * yaw is what makes people sick, which is why this is a snap rather than a ramp in the
 * first place. Also chosen, per the owner, so six flicks close the loop and land back at
 * the starting orientation exactly — a property worth having independent of comfort,
 * because it means a player can always reason about where they end up after a known number
 * of flicks.
 *
 * Was 45 degrees (eight flicks to close the loop) until 2026-08-12, changed on a false
 * premise that this constant alone corrects nothing about: two people measured a full
 * circle at roughly 4.5 flicks against a nominal 45 degrees, and the first fix tried was
 * simply raising this value. The real cause, found afterward by reading
 * `WebXRControllerTeleportation.js` directly, is that Babylon's own teleportation feature
 * applies a *second*, independent snap rotation of its own (`rotationAngle`, defaulting to
 * `Math.PI / 8`, 22.5 degrees) on the identical sideways-stick threshold this method reads —
 * so every flick was already turning 45 + 22.5 = 67.5 degrees, which is what actually
 * explains ~4.5 flicks to close a circle (360 / 67.5 ≈ 5.3, close enough given the ~0.7
 * engage threshold's own slop — not exact, but the right order and the right mechanism,
 * confirmed by zeroing Babylon's own contribution below and nothing else). Raising this
 * constant to `Math.PI / 3` would only have made the double-rotation worse (60 + 22.5 =
 * 82.5 degrees, ≈4.4 flicks) — moving the number without fixing the cause, and landing
 * near the wrong value already being observed. The actual fix is
 * `teleportation.rotationAngle = 0` in `configureTeleportation`, which zeroes Babylon's own
 * contribution so this constant is the *only* source of snap-turn rotation, restoring the
 * property this doc comment describes.
 */
const SNAP_TURN_RADIANS = Math.PI / 3;

/** Push past this to turn, fall below the lower bound to re-arm. Prevents drift-spam. */
const SNAP_ENGAGE = 0.7;
const SNAP_RELEASE = 0.3;

/**
 * Forward on the same stick arms teleportation, so sideways only counts as a turn while
 * the stick is not being pushed forward or back.
 */
const TELEPORT_DEADZONE = 0.35;

/**
 * Eye height, in metres, seated mode targets. The local-floor reference space reports the
 * person's real head height, which sitting down puts at roughly 1.2m — correct, and also
 * below where the room was built to be reached from. See `refreshSeatedOffset`.
 */
const SEATED_EYE_HEIGHT_METRES = 1.7;

/**
 * How long to wait after entering XR before checking whether any controller connected — see
 * `scheduleControllerCheck`. Long enough for Babylon to have walked the session's real input
 * sources at least once; short enough that a genuinely controller-less session is reported
 * before someone has spent much time confused inside it.
 */
const CONTROLLER_CHECK_DELAY_MS = 1500;

export interface XrManagerOptions {
  /**
   * Fired the instant session entry begins — synchronously, on the same tick as the call to
   * `enterAsync()`, before any browser/device negotiation has happened. This is
   * `WebXRState.ENTERING_XR`, not `IN_XR`: Babylon does not swap in the XR camera until
   * `enterXRAsync` has awaited session creation, reference space setup and the render layer,
   * so between this callback and `onEnter` the canvas is still rendering the ordinary desktop
   * view. A caller that wants to cover the canvas for the whole session — not just once it is
   * fully established — has to hook this, not `onEnter`.
   */
  onEnterRequested?: () => void;
  onEnter?: (experience: WebXRDefaultExperience) => void;
  onExit?: () => void;

  /**
   * Fired once `tryGrantPlayAreaBounds` resolves, true or false either way — never left
   * unfired, since "requested but not yet known" and "actively declined" are different
   * facts and a caller reporting status (see ui/SessionScreen.ts) needs to be able to tell
   * them apart. "Not granted" is the common case (no guardian configured) — see
   * `tryGrantPlayAreaBounds`.
   */
  onBoundedFloorChanged?: (granted: boolean) => void;

  /** Fired when the session is usable but something it should have is absent. */
  onSetupIncomplete?: (missing: string[]) => void;

  /**
   * True while the off-hand tray owns the stick for menu navigation, so travel must not also
   * read it — the same physical input cannot steer the person and drive a menu at once.
   *
   * Polled every frame, the same arm's-length shape `ToolPropRenderer`'s own controller-model
   * callback uses, rather than an edge-triggered call when the tray opens or closes. That is
   * deliberate: a poll re-derives the right answer
   * from scratch every frame, so it self-corrects no matter *how* the tray stopped being open —
   * the grip released, the controller disconnected, or the session itself ended — without
   * needing every one of those paths to remember to call a setter.
   */
  travelSuppressed?: () => boolean;
}

/**
 * Immersive session and locomotion.
 *
 * Controllers are required for every in-headset action — see `findMissingCapabilities` below,
 * which reports honestly when a session has none. Hand tracking existed here once
 * (`WebXRHandTracking`, `handSupportOptions`, and a hands-only locomotion fallback,
 * `HandTeleport`) and was removed outright, 2026-08-10: it never worked well and was an
 * awkward fit for a scheme built around grip/trigger and a thumbstick. See CLAUDE.md's
 * "Grip means near, trigger means far" entry for the reversal.
 *
 * Controller locomotion lives entirely on the **off hand**: its thumbstick teleports and snap
 * turns. The dominant hand's stick stays unbound. That is the whole reason this file knows
 * about handedness — bind travel to both sticks and there is no free input left for anything
 * else, which is exactly where M4 ended up.
 *
 * The off hand's first face button used to recentre the player here too. It is gone: the
 * assistant's talk binding (`Grabber.talkButton`, `input/Grabbers.ts`) reads the identical
 * physical button — X on a left Touch controller, A on a right one — so both fired on every
 * single press, and the player recentred every time they meant to talk. Teleport reaches any
 * floor, so recentre was redundant for what it recovered from; plan/ai-in-room.md called for
 * dropping it rather than rebinding it, and this is that fix — see plan/open-items.md,
 * "Recentre has no binding on hands or on desktop", for the closure note.
 *
 * While the off-hand tray is open it borrows this exact stick for menu navigation instead, so
 * travel is suppressed for as long as that lasts — see `travelSuppressed` on
 * `XrManagerOptions` and `syncTravelSuppression` below. Both halves of locomotion read the
 * same physical stick — snap-turn by hand here, teleport inside Babylon's own feature — so
 * both have to stop, not just the one written in this file.
 */
// spec: SPEC-081
export class XrManager {
  private experience: WebXRDefaultExperience | null = null;
  private readonly snapArmed = new WeakMap<WebXRInputSource, boolean>();
  private stopWatchingHandedness: (() => void) | null = null;
  private stopSuppressionSync: (() => void) | null = null;

  /** Cached result of the last `travelSuppressed()` poll, so teleportation is only
   *  attached/detached on an actual change rather than every frame. */
  private travelIsSuppressed = false;

  /** Live controller model per connected hand, kept so `setDominantControllerModelVisible`
   *  has something to hide — see `trackControllerModels`. */
  private readonly controllerModels = new Map<XRHandedness, WebXRAbstractMotionController>();

  /** Hands currently asked to have their controller model hidden. A set of *requests*, not
   *  of applied state, so a request made before a model has finished loading is not lost —
   *  see `trackControllerModels`. */
  private readonly hiddenControllerHands = new Set<XRHandedness>();

  /**
   * Which hand each role's hide request actually hid, remembered from that call rather than
   * re-read from `handedness.dominant`/`handedness.off` on the matching show call — see the
   * comment on `setRoleControllerModelVisible` for why re-reading it live is exactly the bug
   * this map exists to prevent. Keyed by role rather than by hand, so a hide from one role and
   * a hide from the other can never be confused for each other even in the (currently
   * impossible, since dominant and off always name different hands) case that both ever
   * resolved to the same physical hand. Absent for a role currently hiding nothing.
   */
  private readonly roleControllerHiddenFor = new Map<HandRole, XRHandedness>();

  /** Pending `scheduleControllerCheck` timer, or null when none is outstanding — see that
   *  method's own doc. */
  private controllerCheckTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Added to the XR camera's Y position while in seated mode, so a person sitting down gets
   * a standing person's eye height instead of their own ~1.2m. Computed once — target minus
   * the camera's measured `realWorldHeight` — then held rather than recomputed every frame:
   * recomputing every frame would put the person's view on rails, and leaning or sitting up
   * straighter (the whole point of a seated setup) would stop working.
   *
   * `null` in standing mode, and in seated mode before a session has established a value.
   * Re-derived at exactly one moment now — session entry, in `refreshSeatedOffset` — and
   * separately *reapplied* (not re-derived) after a teleport, which would otherwise silently
   * drop it; see the comment in `configureTeleportation`. It used to also re-derive on
   * recentre, which gave a seated person a way to recover from a changed chair or a slump
   * mid-session without leaving the session — recentre is gone (see the class-level doc
   * comment) and nothing replaces that recovery path yet.
   */
  private seatedEyeOffset: number | null = null;

  private constructor(
    private readonly scene: Scene,
    private readonly options: XrManagerOptions,
  ) {}

  /**
   * Feature names the bundle actually registered.
   *
   * Worth being able to check: with tree-shaking, a feature is only available if its module
   * was imported, and a missing import fails silently — the session starts with no
   * teleportation and nothing explains why. This turns that into something observable.
   */
  static availableFeatures(): string[] {
    return WebXRFeaturesManager.GetAvailableFeatures();
  }

  /**
   * True when this browser can actually start an immersive session.
   *
   * The implementation moved to `XrSupport.ts`, which imports nothing at all — see that module
   * for why the capability check must not live on a class that pulls in the whole engine. This
   * stays as the delegating alias, because every existing caller and five test spies
   * (`vi.spyOn(XrManager, 'isSupported')`) name it here, and because anything that has already
   * loaded this class pays nothing for the extra hop.
   */
  static isSupported(): Promise<boolean> {
    return isImmersiveVrSupported();
  }

  /**
   * Sets up the session. Returns null when XR is unavailable rather than throwing, so the
   * caller can fall through to the desktop camera — which is a supported way to run this,
   * not an error path.
   */
  static async createAsync(
    scene: Scene,
    floorMeshes: AbstractMesh[],
    options: XrManagerOptions = {},
  ): Promise<XrManager | null> {
    if (!(await XrManager.isSupported())) {
      return null;
    }

    const manager = new XrManager(scene, options);

    try {
      await manager.initialise(floorMeshes);
    } catch (error) {
      console.warn('WebXR setup failed; continuing on desktop.', error);
      return null;
    }

    return manager;
  }

  get inXr(): boolean {
    return this.experience?.baseExperience.state === WebXRState.IN_XR;
  }

  /**
   * Starts the immersive session.
   *
   * Must be called straight from a user gesture — browsers refuse a session request that
   * is not attributable to a click. That is why the whole experience is built during the
   * splash screen rather than here: by the time this runs there is no async work left to
   * do, so the gesture is still valid.
   */
  async enterAsync(): Promise<void> {
    const experience = this.experience;
    const baseExperience = experience?.baseExperience;

    if (!experience || !baseExperience) {
      throw new Error('WebXR is not available in this browser.');
    }

    const request = this.describeSessionRequest();
    console.info(
      `Requesting immersive-vr — required: [${request.required.join(', ') || 'none'}], ` +
        `optional: [${request.optional.join(', ') || 'none'}]`,
    );

    // local-floor puts the origin on the physical floor, which is what makes room-scale
    // and seated play both work without asking the user to calibrate anything.
    //
    // bounded-floor rides along as an extra *optional* feature of the session itself here —
    // not as the reference space this call requests, and not swapped in for 'local-floor'
    // above. Those would look equivalent and are not: see tryGrantPlayAreaBounds, called
    // below, for the second and separate step that actually asks for bounded-floor, and for
    // why folding it into this one call the obvious way is exactly the bug
    // plan/play-area.md warns about. Declaring it here only reserves the session's
    // permission to request it later — without this, session.requestReferenceSpace
    // ('bounded-floor') below is refused outright, guardian or not.
    await baseExperience.enterXRAsync('immersive-vr', 'local-floor', experience.renderTarget, {
      optionalFeatures: ['bounded-floor'],
    });

    // What was asked for and what was granted are different things. This is the only honest
    // source for what a real session actually came back with.
    const granted = baseExperience.sessionManager.session?.enabledFeatures;

    if (granted !== undefined) {
      console.info('Session granted features:', [...granted].join(', '));
    }

    await this.tryGrantPlayAreaBounds();
  }

  async exitAsync(): Promise<void> {
    await this.experience?.baseExperience.exitXRAsync();
  }

  get camera() {
    return this.experience?.baseExperience.camera ?? null;
  }

  dispose(): void {
    this.stopWatchingHandedness?.();
    this.stopWatchingHandedness = null;
    this.stopSuppressionSync?.();
    this.stopSuppressionSync = null;
    this.cancelControllerCheck();
    this.controllerModels.clear();
    this.hiddenControllerHands.clear();
    this.roleControllerHiddenFor.clear();
    this.experience?.dispose();
    this.experience = null;
  }

  private async initialise(floorMeshes: AbstractMesh[]): Promise<void> {
    const experience = await WebXRDefaultExperience.CreateAsync(this.scene, {
      floorMeshes,
      disableTeleportation: false,

      // Teleport belongs to the off hand alone. Left unset, Babylon attaches it to every
      // controller, and a dominant-hand stick that also teleports is a stick that cannot be
      // used for anything else.
      teleportationOptions: { forceHandedness: handedness.off },
      // Babylon's floating "Enter VR" button is suppressed: entry belongs to the splash
      // screen, where the user also picks a name and can see whether a headset was found.
      disableDefaultUI: true,
    });

    this.experience = experience;

    // CreateAsync catches its own errors, logs them through Babylon's Logger, and returns a
    // partially-built experience. So "it resolved" says nothing about whether locomotion
    // exists — that has to be checked, or a broken build presents as a working one.
    const missing = this.findMissingCapabilities();

    if (missing.length > 0) {
      console.error(
        `WebXR setup is incomplete: ${missing.join(', ')}. ` +
          'Usually a missing side-effect import in XrManager.ts — see the notes on the ' +
          'imports at the top of this file.',
      );

      this.options.onSetupIncomplete?.(missing);
    }

    this.configureTeleportation();
    this.bindOffHandControls();
    this.trackControllerModels();

    // The setting can be changed on the entry screen after this has already been built, so
    // teleport has to be able to move to the other controller without a reload.
    this.stopWatchingHandedness = handedness.onChanged(() => this.applyHandedness());

    // Polled once per frame for as long as the manager exists, in or out of a session — see
    // the note on `travelSuppressed` above for why a poll rather than an edge.
    const suppressionObserver = this.scene.onBeforeRenderObservable.add(() =>
      this.syncTravelSuppression(),
    );
    this.stopSuppressionSync = () =>
      this.scene.onBeforeRenderObservable.remove(suppressionObserver);

    // ENTERING_XR fires synchronously from enterXRAsync, before it awaits anything — see
    // the note on onEnterRequested above for why that matters. EXITING_XR is deliberately
    // not hooked to anything: the XR camera is still active and the canvas is still showing
    // its view right up until NOT_IN_XR, so uncovering any earlier would flash that instead
    // of restoring the desktop view it is about to become.
    experience.baseExperience.onStateChangedObservable.add((state) => {
      if (state === WebXRState.ENTERING_XR) {
        this.options.onEnterRequested?.();
      } else if (state === WebXRState.IN_XR) {
        // Not sooner: enterXRAsync's own promise resolves before the first XR frame has run
        // (confirmed by reading webXRExperienceHelper.js — it flips the state only inside an
        // onXRFrameObservable.addOnce), and realWorldHeight needs that first frame to have
        // happened. WebXRCamera's own per-frame handler registers with insertFirst, so by the
        // time this fires — on that same first frame — it has already run.
        this.establishSeatedOffset(experience.baseExperience.camera);
        this.options.onEnter?.(experience);
        this.scheduleControllerCheck();
      } else if (state === WebXRState.NOT_IN_XR) {
        this.cancelControllerCheck();
        this.options.onExit?.();
      }
    });
  }

  /**
   * Controllers are now required for every in-headset action — hand tracking, the fallback
   * that used to cover a controller-less session, is gone (see the class doc comment). A
   * session with none connected has to say so plainly rather than silently presenting a room
   * nothing can act in.
   *
   * Input sources are not necessarily attached the instant `IN_XR` fires — `onEnter` above
   * runs on the very first XR frame, before Babylon has necessarily finished walking
   * `session.inputSources` into `experience.input.controllers` — so this checks once, after a
   * short grace period, rather than at `IN_XR` itself. A real absence (no controllers powered
   * on, or a headset in hand-tracking-only mode) is still absent after that delay; a momentary
   * connection race is not.
   */
  private scheduleControllerCheck(): void {
    this.cancelControllerCheck();

    this.controllerCheckTimer = setTimeout(() => {
      this.controllerCheckTimer = null;

      if ((this.experience?.input.controllers.length ?? 0) === 0) {
        console.error('No WebXR controllers connected; nothing in this session can be acted on.');
        this.options.onSetupIncomplete?.(['controllers']);
      }
    }, CONTROLLER_CHECK_DELAY_MS);
  }

  private cancelControllerCheck(): void {
    if (this.controllerCheckTimer !== null) {
      clearTimeout(this.controllerCheckTimer);
      this.controllerCheckTimer = null;
    }
  }

  private configureTeleportation(): void {
    const teleportation = this.experience?.teleportation;

    if (!teleportation) {
      return;
    }

    // Floor meshes are deliberately not re-added here. They were passed to CreateAsync,
    // and the teleportation feature keeps that *same array instance* rather than copying
    // it — so calling addFloorMesh in a loop over it appends to the array being iterated
    // and never terminates, until push finally throws RangeError.
    //
    // The aliasing is useful and load-bearing: SceneRoot mutates its floorMeshes in place
    // when a room is rebuilt, so teleport targets follow automatically. Never reassign
    // that array.

    // Choosing the facing direction as part of the jump means arriving already oriented,
    // which saves a separate turn on landing. `rotationEnabled` governs only that — whether a
    // *forward teleport* uses the stick's own direction to pick a landing orientation — and has
    // no bearing on the line right below it.
    teleportation.rotationEnabled = true;

    // Zeroes Babylon's own snap-turn, which the feature applies unconditionally once attached
    // (`WebXRControllerTeleportation.js`'s rotation block, `rotationAngle`, defaulting to
    // Math.PI / 8 — no `rotationEnabled` guard on it at all, despite sitting right beside the
    // flag above). Left at its default, every off-hand sideways flick fired *both* that
    // rotation and this class's own hand-written `handleThumbstick` snap-turn on the identical
    // stick threshold, silently doubling every turn (SNAP_TURN_RADIANS's own doc has the full
    // arithmetic and how this was found). This line and `rotationEnabled` above look like a
    // contradiction — rotation explicitly zeroed sitting right next to rotation explicitly
    // enabled — and are not one: `rotationEnabled` is "does a forward teleport also reorient
    // you," `rotationAngle` is "how far does a sideways flick turn you," and this project wants
    // the first and not the second, because the second is fully reimplemented by hand right
    // here in this class, deliberately, for guards Babylon's own version does not carry
    // (`bindOffHandControls`'s own doc above has the list). Do not "clean this up" back to
    // Babylon's default without reading both.
    teleportation.rotationAngle = 0;

    teleportation.parabolicRayEnabled = true;
    teleportation.parabolicCheckRadius = 5;

    // THE TRAP: WebXRMotionControllerTeleportation._teleportForward relocates the player by
    // writing xrCamera.position.copyFrom(teleportationTargetMesh.position) — the target sits
    // on the floor — and then adds the camera's own realWorldHeight back on top (confirmed by
    // reading WebXRControllerTeleportation.js). That reconstructs "floor plus real head
    // height" correctly, but "real head height" is the raw, un-offset figure; it has no idea
    // about seatedEyeOffset above and silently drops it. A teleport is not one of the two
    // moments ("Establish"/"re-establish", see refreshSeatedOffset) this offset is supposed to
    // change — only one it has to survive — so this reapplies the held value rather than
    // re-deriving it, which would also carry across whatever lean was in realWorldHeight at
    // the moment of landing.
    //
    // onAfterCameraTeleport fires synchronously, inside the same call that just clobbered the
    // offset, so this write lands before anything else reads camera.position — in particular
    // before WebXRCamera._updateReferenceSpace's next pass, which is what would otherwise fold
    // the uncorrected height into the live reference space and make it stick.
    teleportation.onAfterCameraTeleport.add(() => {
      const offset = this.seatedEyeOffset;
      const teleportedCamera = this.experience?.baseExperience.camera;

      if (offset !== null && teleportedCamera) {
        teleportedCamera.position.y += offset;
      }
    });
  }

  /**
   * Capabilities that should exist after setup but do not.
   *
   * Checks the constructed objects rather than the feature registry: a feature module can
   * be registered and still fail to construct, which is precisely the failure that
   * `availableFeatures()` cannot see.
   */
  private findMissingCapabilities(): string[] {
    const experience = this.experience;
    const missing: string[] = [];

    if (!experience) {
      return ['the experience itself'];
    }

    if (!experience.baseExperience) {
      missing.push('base experience');
    }

    if (!experience.input) {
      missing.push('controller input');
    }

    // Locomotion. Without it you can look around a room you cannot cross, which for a
    // space you are meant to walk about in is close to useless.
    if (!experience.teleportation) {
      missing.push('teleportation');
    }

    if (!experience.pointerSelection) {
      missing.push('pointer selection');
    }

    return missing;
  }

  /**
   * What the session request will ask for, split by whether the headset is allowed to
   * refuse it.
   *
   * Exists because a single wrongly-required feature makes `requestSession` reject the
   * whole session with one opaque message and no indication of which feature was at fault.
   * Logging this before entry turns that into something readable.
   */
  describeSessionRequest(): { required: string[]; optional: string[] } {
    const featuresManager = this.experience?.baseExperience.featuresManager;

    if (!featuresManager) {
      return { required: [], optional: [] };
    }

    // `required` per feature is not exposed publicly, and this is diagnostics only.
    const registry = (
      featuresManager as unknown as {
        _features: Record<string, { required?: boolean; featureImplementation?: { xrNativeFeatureName?: string } }>;
      }
    )._features;

    const required: string[] = [];
    const optional: string[] = [];

    for (const name of featuresManager.getEnabledFeatures()) {
      // Only features with a native name reach the session request at all; the rest
      // (teleportation, pointer selection) are pure Babylon and cannot affect it.
      const nativeName = registry?.[name]?.featureImplementation?.xrNativeFeatureName;

      if (nativeName === undefined || nativeName.length === 0) {
        continue;
      }

      (registry[name]?.required === true ? required : optional).push(nativeName);
    }

    return { required, optional };
  }

  /**
   * Snap-turn, on the off hand.
   *
   * Written by hand for two guards Babylon's own teleportation-feature snap rotation
   * (`WebXRControllerTeleportation`'s `rotationAngle`, applied on the same sideways-stick
   * threshold) does not carry: `travelIsSuppressed`, so the tray owns the stick while open, and
   * `TELEPORT_DEADZONE` on the y axis, so a forward-ish flick is not also read as a turn. This
   * used to say Babylon "ships teleportation and smooth movement but no snap-turn" — that was
   * false, and finding out how (see `SNAP_TURN_RADIANS`'s own doc: Babylon's own snap rotation
   * was firing on every flick alongside this one, undetected, because the comment said it
   * couldn't be) is why this paragraph now says exactly what this hand-written version adds
   * instead of restating a reason that never held.
   *
   * Observers are registered for every controller and the handedness test happens *inside*
   * them rather than here. Registration happens once, when a controller connects; the setting
   * can change afterwards, and a test done at registration time would freeze the bindings to
   * whichever hand was dominant at that moment.
   *
   * Used to also bind the off hand's first face button (the profile's first plain button — X
   * on a left Touch controller, A on a right one) to `recenter`. That method and this binding
   * are both gone now — see the class-level doc comment above for why.
   */
  private bindOffHandControls(): void {
    const experience = this.experience;

    if (!experience) {
      return;
    }

    experience.input.onControllerAddedObservable.add((controller) => {
      controller.onMotionControllerInitObservable.add((motionController) => {
        const thumbstick =
          motionController.getComponentOfType('thumbstick') ??
          motionController.getComponentOfType('touchpad');

        thumbstick?.onAxisValueChangedObservable.add((axes) => {
          this.handleThumbstick(controller, axes.x, axes.y);
        });
      });
    });
  }

  /**
   * Keeps a live reference to each connected controller's motion controller, so
   * `setDominantControllerModelVisible`/`setOffControllerModelVisible` have a `rootMesh` to hide.
   *
   * Controller models come from the WebXR input-profile registry as `.glb` files, loaded and
   * parented under the motion controller's own `rootMesh` by Babylon's controller-model feature
   * (`webXRAbstractMotionController.ts`, `initControllerMesh`) — this class never builds or
   * loads anything itself, only toggles `rootMesh.setEnabled(...)` on the mesh Babylon already
   * created. Hiding rather than disposing is deliberate: the model has to come back exactly as
   * it was — same mesh, no reload — the moment nothing is holding the hand any more.
   *
   * Wired to two held-prop renderers, each hiding the physical hand it occupies while it
   * occupies it: `ToolPropRenderer` (main.ts) hides the dominant hand's controller model while
   * a pen, the eraser or the scanner is rendered there instead — restoring the visual half of
   * "the mode is what is in your dominant hand" that M12A's `PenLedge` removal had dropped along
   * with the ledge (see this class's own history: the mechanism below shipped in M11, went
   * briefly unused across M12A/M12B, and was rewired in #79). `TrashCanRenderer` (main.ts) hides
   * the off hand's controller model the same way while the bin is shown, so a held node's
   * delete gesture reads as a real bin replacing the hand rather than a bin floating in front of
   * it — reported twice from a headset before this existed (CLAUDE.md has no entry for this yet;
   * see the PR that added it).
   *
   * `onMotionControllerInitObservable` fires once the motion controller object exists but
   * before its model has necessarily finished loading — `rootMesh` is null until then (its
   * own doc comment: "null if the model was not yet initialized"). Re-applying whatever
   * visibility is currently wanted on `onModelLoadedObservable` too covers a request made
   * before the glTF has arrived, so it is not silently lost.
   */
  private trackControllerModels(): void {
    const experience = this.experience;

    if (!experience) {
      return;
    }

    experience.input.onControllerAddedObservable.add((controller) => {
      controller.onMotionControllerInitObservable.add((motionController) => {
        const hand = controller.inputSource.handedness;

        this.controllerModels.set(hand, motionController);
        this.applyControllerVisibility(hand);

        motionController.onModelLoadedObservable.add(() => this.applyControllerVisibility(hand));
      });
    });

    experience.input.onControllerRemovedObservable.add((controller) => {
      this.controllerModels.delete(controller.inputSource.handedness);
    });
  }

  private applyControllerVisibility(hand: XRHandedness): void {
    const visible = !this.hiddenControllerHands.has(hand);
    this.controllerModels.get(hand)?.rootMesh?.setEnabled(visible);
  }

  /**
   * Shows or hides the rendered controller model for the dominant hand — `ToolPropRenderer`'s
   * own callback (main.ts), hiding it for as long as a pen, the eraser or the scanner is
   * rendered there instead. A thin, role-fixed wrapper over `setRoleControllerModelVisible`; see
   * that method's own doc for the handedness-flip correctness property this shares with
   * `setOffControllerModelVisible` below.
   */
  setDominantControllerModelVisible(visible: boolean): void {
    this.setRoleControllerModelVisible('dominant', visible);
  }

  /**
   * Shows or hides the rendered controller model for the off hand — `TrashCanRenderer`'s own
   * callback (main.ts), hiding it for as long as the bin is shown in its place. The off-hand
   * counterpart to `setDominantControllerModelVisible` above; see `setRoleControllerModelVisible`
   * for the shared mechanism and its handedness-flip correctness property.
   */
  setOffControllerModelVisible(visible: boolean): void {
    this.setRoleControllerModelVisible('off', visible);
  }

  /**
   * Shared mechanism behind `setDominantControllerModelVisible`/`setOffControllerModelVisible`.
   *
   * `handedness.dominant`/`handedness.off` is read fresh only on the *hiding* (`visible: false`)
   * call, and remembered in `roleControllerHiddenFor` under `role` — the matching *showing* call
   * reveals that same hand, regardless of whatever the live setting says by then, rather than
   * asking again. Reading it fresh on every call — matching the "ask on every read" rule
   * `Grabber.role` follows (Handedness.ts) — is right for a role lookup that resolves and is
   * used in the same instant, but handedness is a live setting a person can flip mid-hold, and a
   * held prop would not change physical hand when they do. A hide and its matching show, each
   * re-reading the live setting independently, can then name two different hands — the one
   * actually hidden is never told to reveal itself again, leaking hidden "until the session
   * ends." Keying the remembered hand by `role` rather than sharing one field between the two
   * callers is what lets a dominant-hand hide/show pair and an off-hand hide/show pair be
   * outstanding at the same time without either clobbering the other's bookkeeping.
   *
   * A safe no-op outside a session, before that hand's controller model has finished loading,
   * or for a tracked hand, which has no controller model to hide at all — every one of those
   * is just an absent map entry, not a special case. Also a safe no-op for a `visible: true`
   * call when nothing is currently hidden through this role — nothing to reveal.
   */
  private setRoleControllerModelVisible(role: HandRole, visible: boolean): void {
    if (visible) {
      const hand = this.roleControllerHiddenFor.get(role);

      if (hand === undefined) {
        return;
      }

      this.roleControllerHiddenFor.delete(role);
      this.hiddenControllerHands.delete(hand);
      this.applyControllerVisibility(hand);
      return;
    }

    const hand = role === 'dominant' ? handedness.dominant : handedness.off;
    this.roleControllerHiddenFor.set(role, hand);
    this.hiddenControllerHands.add(hand);
    this.applyControllerVisibility(hand);
  }

  private isOffHand(controller: WebXRInputSource): boolean {
    return controller.inputSource.handedness === handedness.off;
  }

  private handleThumbstick(controller: WebXRInputSource, x: number, y: number): void {
    const camera = this.experience?.baseExperience.camera;

    // This class never binds the dominant hand's stick to locomotion — see the module doc
    // above. It is not unbound in the app overall: `AuthoringController.tickToolCycle` reads
    // it directly (grip+stick switches mode; bare stick drives whichever axis the current mode
    // defines), the mode-hierarchy redesign this file's own module doc references. The original
    // plan named here — push/pull and rotate of a held node — was never built.
    if (!camera || !this.isOffHand(controller)) {
      return;
    }

    const armed = this.snapArmed.get(controller) ?? true;

    if (Math.abs(x) < SNAP_RELEASE) {
      this.snapArmed.set(controller, true);
      return;
    }

    // The off-hand tray owns this exact stick while it is open — see syncTravelSuppression.
    // Checked after the re-arm above rather than before, so a stick that returns to centre
    // while the tray is open still re-arms normally; only the turn itself is what gets
    // skipped, which means snapArmed already reflects the stick's real position by the time
    // suppression lifts, rather than whatever it was frozen at when the tray opened.
    if (this.travelIsSuppressed) {
      return;
    }

    // Forward or back on this stick is a teleport gesture; don't also read it as a turn.
    if (!armed || Math.abs(x) < SNAP_ENGAGE || Math.abs(y) > TELEPORT_DEADZONE) {
      return;
    }

    this.snapArmed.set(controller, false);

    const angle = x > 0 ? SNAP_TURN_RADIANS : -SNAP_TURN_RADIANS;

    Quaternion.FromEulerAngles(0, angle, 0).multiplyToRef(
      camera.rotationQuaternion,
      camera.rotationQuaternion,
    );
  }

  /**
   * Recomputes the seated offset from the camera's current `realWorldHeight` and stores it.
   *
   * `realWorldHeight` reads the pose against the session's `baseReferenceSpace` (confirmed by
   * reading webXRCamera.js), which is set once when the session starts and never reassigned —
   * unlike `sessionManager.referenceSpace`, which teleport and snap-turn both offset out from
   * under the camera. So this is always the raw, real physical head height, whatever
   * locomotion has done since the session began, which is exactly what "target minus measured"
   * needs to be measured against. Standing mode stores (and returns) null, which every caller
   * below treats as "add nothing" — the mechanism standing mode's own behaviour stays
   * unchanged through.
   */
  private refreshSeatedOffset(camera: WebXRCamera): number | null {
    this.seatedEyeOffset = seated.value ? SEATED_EYE_HEIGHT_METRES - camera.realWorldHeight : null;
    return this.seatedEyeOffset;
  }

  /**
   * Establishes the seated offset for a freshly-entered session and applies it immediately, by
   * rewriting the camera's Y outright rather than adding to whatever it already holds — that
   * makes this independent of exactly when in Babylon's own first-frame sequence it runs,
   * rather than relying on `position.y` already equalling `realWorldHeight` at this point (it
   * does, but this does not need to assume so). Only Y is touched; X/Z came from the
   * pre-session camera via Babylon's own first-frame handling and are left alone.
   */
  private establishSeatedOffset(camera: WebXRCamera): void {
    const offset = this.refreshSeatedOffset(camera);

    if (offset !== null) {
      camera.position.y = camera.realWorldHeight + offset;
    }
  }

  /**
   * The second, separate step of the two-step bounded-floor request — see the note in
   * `enterAsync` for the first half.
   *
   * Calls `session.requestReferenceSpace('bounded-floor')` directly, never
   * `sessionManager.setReferenceSpaceTypeAsync`. On a real device without a configured
   * guardian — the common case — that call rejects, and `setReferenceSpaceTypeAsync`'s own
   * catch block does not fall back to `local-floor`; it drops all the way to `viewer`,
   * offsetting a guessed height to stand in for a floor that no longer has an origin. That
   * would break floor height for every standing user on such a headset, not just cost them a
   * grant. Calling `requestReferenceSpace` directly sidesteps that fallback entirely: a
   * rejection here is caught below and leaves the already-granted local-floor session
   * exactly as it was.
   *
   * The granted space itself is never assigned to
   * `baseExperience.sessionManager.referenceSpace` — the reference space the camera,
   * controllers and teleportation all read every frame — nor kept at all. It used to be held
   * onto so a play-area outline could read `boundsGeometry` off it and cross that into room
   * space for drawing, and recentre could target its centroid; both were removed after a
   * headset test found the crossing was done once, at grant time, and never redone as the
   * player moved — see plan/play-area.md, "Removed after a headset test". What is left, and
   * still worth reporting, is only whether the request succeeded at all: `onBoundedFloorChanged`
   * says so — "requested" is knowable before the session exists, "granted" is not.
   */
  private async tryGrantPlayAreaBounds(): Promise<void> {
    const session = this.experience?.baseExperience.sessionManager.session;

    if (!session) {
      return;
    }

    try {
      await session.requestReferenceSpace('bounded-floor');
      this.options.onBoundedFloorChanged?.(true);
    } catch (error) {
      // Not surfaced through onSetupIncomplete: that channel means the build is broken — a
      // missing side-effect import, a feature that should exist and does not — and a
      // headset with no guardian configured is neither. It is the common case, not a
      // degraded one.
      console.info(
        'bounded-floor was not granted; continuing on local-floor with no play-area bounds. ' +
          'This is expected on any headset without a configured guardian.',
        error,
      );

      this.options.onBoundedFloorChanged?.(false);
    }
  }

  /**
   * Detaches or reattaches Babylon's own teleportation feature to match `travelSuppressed()`.
   *
   * Snap-turn guards itself with the same flag inline in `handleThumbstick`, but teleportation
   * is not written here — it is Babylon's `WebXRMotionControllerTeleportation`, reading the
   * off-hand stick internally the moment it is attached, with nothing exposed to tell it "not
   * right now". Detaching is the only lever available, and `applyHandedness` above already
   * proves detach-then-attach is safe on this exact feature.
   *
   * Only acts on a change, so an already-detached feature is not asked to detach again every
   * frame — `attach()`/`detach()` are cheap but not nothing, and this runs every frame for as
   * long as the manager exists.
   */
  private syncTravelSuppression(): void {
    const suppressed = this.options.travelSuppressed?.() ?? false;

    if (suppressed === this.travelIsSuppressed) {
      return;
    }

    this.travelIsSuppressed = suppressed;

    const teleportation = this.experience?.teleportation;

    if (!teleportation) {
      return;
    }

    if (suppressed && teleportation.attached) {
      teleportation.detach();
    } else if (!suppressed && !teleportation.attached) {
      teleportation.attach();
    }
  }

  /**
   * Moves controller teleportation to whichever hand is now the off hand.
   *
   * `forceHandedness` is read when a controller attaches and is not exposed for writing, so
   * the stored options have to be changed and the controllers re-attached: detach drops them,
   * attach walks the current input sources again. Private-field access, guarded, because the
   * alternative is rebuilding the whole experience to change one setting.
   */
  private applyHandedness(): void {
    const teleportation = this.experience?.teleportation;

    if (!teleportation) {
      return;
    }

    const options = (
      teleportation as unknown as { _options?: { forceHandedness?: XRHandedness } }
    )._options;

    if (!options) {
      console.warn('Could not move teleportation to the other hand; Babylon internals moved.');
      return;
    }

    options.forceHandedness = handedness.off;

    if (teleportation.attached) {
      teleportation.detach();
      teleportation.attach();
    }
  }

  /** Head and wrist poses, for streaming to other peers. Wrists are null when untracked. */
  readPose(): {
    head: { p: number[]; r: number[] };
    left: { p: number[]; r: number[] } | null;
    right: { p: number[]; r: number[] } | null;
  } | null {
    const experience = this.experience;
    const camera = experience?.baseExperience.camera;

    if (!experience || !camera) {
      return null;
    }

    const packPose = (position: Vector3, rotation: Quaternion) => ({
      p: vec3ToArray(position),
      r: quatToArray(rotation),
    });

    let left: { p: number[]; r: number[] } | null = null;
    let right: { p: number[]; r: number[] } | null = null;

    for (const controller of experience.input.controllers) {
      const pointer = controller.pointer;
      const rotation = pointer.rotationQuaternion ?? Quaternion.Identity();
      const pose = packPose(pointer.position, rotation);

      if (controller.inputSource.handedness === 'left') {
        left = pose;
      } else if (controller.inputSource.handedness === 'right') {
        right = pose;
      }
    }

    return {
      head: packPose(camera.position, camera.rotationQuaternion),
      left,
      right,
    };
  }
}
